import { useCallback, useEffect, useRef, useState } from 'react';

import { api, type InstallerInfo, type InviteResult, type User } from './api.js';
import { EmptyState, UserIcon, useToast } from './ui.js';

/**
 * The "App" console section: publish the desktop installer, and hand it out.
 *
 * Two distinct jobs live here. Publishing (upload a build, see what is current)
 * is an occasional admin task. Handing out (invite a user, copy a link) happens
 * per person and also lives as a button on each row of the Users table — the
 * InviteDialog below is shared by both.
 */

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

// ---------------------------------------------------------------------------
// Shared invite / link dialog
// ---------------------------------------------------------------------------

export function InviteDialog({
  user,
  onClose,
}: {
  /** A specific user to invite, or null for an ad-hoc share link. */
  user: User | null;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const [result, setResult] = useState<InviteResult | { link: string; expiresAt: string } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(() => {
    setBusy(true);
    setError(null);
    const p = user ? api.inviteUser(user.id, 5) : api.createInstallerLink(null);
    void p
      .then((r) => setResult(r))
      .catch((e) => setError(String(e)))
      .finally(() => setBusy(false));
  }, [user]);

  // Generate immediately on open — the admin opened this to get a link.
  useEffect(generate, [generate]);

  const emailed = result && 'emailed' in result ? result.emailed : false;
  const mailto = result && 'mailto' in result ? result.mailto : undefined;

  return (
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Invite"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{user ? `Send the app to ${user.displayName}` : 'Create a share link'}</h3>

        {busy && <p className="section-sub">Generating a secure link…</p>}
        {error && <p className="error">{error}</p>}

        {result && (
          <>
            {user && (
              <p className="section-sub">
                {emailed
                  ? `Emailed to ${user.email}. You can also share the link directly.`
                  : 'Email is not configured, so copy this link and send it to them.'}
              </p>
            )}

            <label className="detail-label" htmlFor="invite-link">
              Download link
            </label>
            <div className="inline-form">
              <input
                id="invite-link"
                readOnly
                value={result.link}
                onFocus={(e) => e.currentTarget.select()}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(result.link);
                  toast.push({ title: 'Link copied', tone: 'ok' });
                }}
              >
                Copy
              </button>
            </div>

            <p className="hint">
              Expires {new Date(result.expiresAt).toLocaleDateString()}.
              {user ? ' The link is personal and stops working after a few installs.' : ''}
            </p>

            {mailto && !emailed && (
              <p style={{ marginTop: 12 }}>
                <a className="link" href={mailto}>
                  Open a pre-filled email…
                </a>
              </p>
            )}
          </>
        )}

        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The App section
// ---------------------------------------------------------------------------

export function AppDownloads(): JSX.Element {
  const toast = useToast();
  const [info, setInfo] = useState<InstallerInfo | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const exeInput = useRef<HTMLInputElement>(null);
  const ymlInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    void api
      .installer()
      .then(setInfo)
      .catch((e) => toast.error(String(e)));
  }, [toast]);

  useEffect(load, [load]);

  async function download(): Promise<void> {
    setDownloading(true);
    try {
      await api.downloadInstaller();
      toast.push({ title: 'Download started', tone: 'ok' });
    } catch (error) {
      toast.error(String(error));
    } finally {
      setDownloading(false);
    }
  }

  async function upload(): Promise<void> {
    const file = exeInput.current?.files?.[0];
    if (!file) {
      toast.error('Choose an installer file first.');
      return;
    }
    const latest = ymlInput.current?.files?.[0] ?? null;
    const v = version || /(\d+\.\d+\.\d+)/.exec(file.name)?.[1] || '';
    setProgress(0);
    try {
      await api.uploadInstaller(file, v, notes, latest, setProgress);
      toast.push({ title: 'Build published', tone: 'ok' });
      setVersion('');
      setNotes('');
      if (exeInput.current) exeInput.current.value = '';
      if (ymlInput.current) ymlInput.current.value = '';
      load();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setProgress(null);
    }
  }

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>App</h2>
          <p className="section-sub">
            Publish the Windows desktop app and send it to your team. A link always serves
            the current build, so an invite sent last week installs this week&apos;s version.
          </p>
        </div>
        <div>
          <button type="button" className="secondary" onClick={() => setLinkOpen(true)}>
            Create share link
          </button>
        </div>
      </div>

      {/* Current build */}
      {info === null ? (
        <p className="section-sub">Loading…</p>
      ) : info.installed ? (
        <div className="card">
          <div className="detail-header">
            <div>
              <div className="row-title">Current build</div>
              <div className="cell-dim">
                Version {info.version} · {humanSize(info.size ?? 0)} · uploaded{' '}
                {info.uploadedAt ? new Date(info.uploadedAt).toLocaleString() : '—'}
              </div>
            </div>
            <button type="button" disabled={downloading} onClick={() => void download()}>
              {downloading ? 'Starting download…' : 'Download for Windows'}
            </button>
          </div>
          {info.notes && <p className="section-sub">{info.notes}</p>}
          <div className="detail">
            <span className="detail-label">SHA-256</span>
            <code className="cell-mono" style={{ wordBreak: 'break-all', fontSize: 12 }}>
              {info.sha256}
            </code>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<UserIcon size={22} />}
          title="No build published yet"
          body="Build the installer with npm run package, then upload the .exe (and its latest.yml) below."
        />
      )}

      {/* Upload a new build */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="row-title">Publish a new build</div>
        <p className="section-sub">
          Upload the <code>.exe</code> from <code>desktop/release</code>. Include{' '}
          <code>latest.yml</code> too so installed apps auto-update.
        </p>

        <div className="form">
          <label className="detail-label">
            Installer (.exe)
            <input ref={exeInput} type="file" accept=".exe,.zip" disabled={progress !== null} />
          </label>
          <label className="detail-label">
            Update manifest (latest.yml, optional)
            <input ref={ymlInput} type="file" accept=".yml,.yaml" disabled={progress !== null} />
          </label>
          <label className="detail-label">
            Version
            <input
              type="text"
              placeholder="e.g. 0.1.0 (read from the filename if left blank)"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              disabled={progress !== null}
            />
          </label>
          <label className="detail-label">
            Notes (optional)
            <input
              type="text"
              placeholder="What changed in this build"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={progress !== null}
            />
          </label>
        </div>

        {progress !== null && (
          <div className="tx-progress" style={{ marginTop: 12 }}>
            <div className="tx-progress-head">
              <span>Uploading…</span>
              <span className="tx-eta">{Math.round(progress * 100)}%</span>
            </div>
            <div className="tx-bar" role="progressbar" aria-valuenow={Math.round(progress * 100)}>
              <div className="tx-bar-fill" style={{ width: `${progress * 100}%` }} />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={() => void upload()} disabled={progress !== null}>
            {progress !== null ? 'Uploading…' : 'Publish build'}
          </button>
        </div>
      </div>

      {linkOpen && <InviteDialog user={null} onClose={() => setLinkOpen(false)} />}
    </section>
  );
}
